
import React, { useState, useEffect } from 'react';
import { 
  Tabs, 
  TabsContent, 
  TabsList, 
  TabsTrigger 
} from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { 
  ChevronRight,
  Clock,
  AlertTriangle,
  ArrowRight,
  Package
} from "lucide-react";
import { OrderStatus, ORDER_STATUS_LABELS, ORDER_STATUS_COLORS } from "@/utils/orderStatus";
import { OrderStatusActions } from "./OrderStatusActions";
import { isAfter, subHours, parseISO, isValid } from "date-fns";
import { useIsMobile } from "@/hooks/use-mobile";
import { Link } from "react-router-dom";
import { formatTimeRange } from "@/utils/formatTime";
import { formatDateAndTime, formatDate } from "@/utils/formatDate";
import { createLogger } from "@/utils/logger";

const logger = createLogger('OrdersManagement');

interface OrderItem {
  id: string;
  meal_id: string;
  quantity: number;
  price: number;
  subtotal: number;
  meal: {
    name: string;
    image?: string;
  };
}

interface Order {
  id: string;
  status: OrderStatus;
  foodie: {
    first_name: string;
    last_name: string;
  };
  meal: {
    name: string;
  };
  quantity: number;
  scheduled_date?: string | null;
  scheduled_time?: string;
  delivery_type: "pickup" | "delivery";
  pickup_window_start?: string | null;
  pickup_window_end?: string | null;
  delivery_address?: string | null;
  ready_at?: string | null;
  ready_by?: string | null;
  created_at: string;
  order_items?: OrderItem[]; // New field for multiple items
}

interface OrdersManagementProps {
  orders: Order[];
  onStatusChange?: (orderId: string, newStatus: OrderStatus) => void;
  isLoading?: boolean;
}

export const OrdersManagement: React.FC<OrdersManagementProps> = ({
  orders,
  onStatusChange,
  isLoading = false
}) => {
  const [activeTab, setActiveTab] = useState<string>('in-progress');
  const [selectedOrders, setSelectedOrders] = useState<Set<string>>(new Set());
  const [bulkActionStatus, setBulkActionStatus] = useState<OrderStatus | ''>('');
  const isMobile = useIsMobile();
  
  const filteredOrders = orders.filter(order => order.status !== 'incomplete');
  
  const handleStatusChange = (orderId: string, newStatus: OrderStatus) => {
    if (onStatusChange) {
      onStatusChange(orderId, newStatus);
    }
    setSelectedOrders(prev => {
      const newSet = new Set(prev);
      newSet.delete(orderId);
      return newSet;
    });
  };

  const handleOrderSelection = (orderId: string, checked: boolean) => {
    setSelectedOrders(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(orderId);
      } else {
        newSet.delete(orderId);
      }
      return newSet;
    });
  };

  const handleBulkStatusUpdate = async () => {
    if (!bulkActionStatus) return;
    
    for (const orderId of selectedOrders) {
      const order = filteredOrders.find(o => o.id === orderId);
      if (order && canTransitionTo(order.status, bulkActionStatus)) {
        await handleStatusChange(orderId, bulkActionStatus);
      }
    }
    
    setSelectedOrders(new Set());
    setBulkActionStatus('');
  };

  const canTransitionTo = (currentStatus: OrderStatus, newStatus: OrderStatus) => {
    const allowedTransitions: Record<OrderStatus, OrderStatus[]> = {
      incomplete: ["in_progress", "cancelled"],
      in_progress: ["ready", "cancelled"],
      ready: ["completed", "cancelled"],
      completed: [],
      cancelled: []
    };
  
    return allowedTransitions[currentStatus]?.includes(newStatus) || false;
  };

  const sortedOrders = React.useMemo(() => {
    return [...filteredOrders].sort((a, b) => {
      if (!a.scheduled_date && !b.scheduled_date) return 0;
      if (!a.scheduled_date) return 1;
      if (!b.scheduled_date) return -1;

      const dateA = parseISO(a.scheduled_date);
      const dateB = parseISO(b.scheduled_date);
      
      if (!isValid(dateA) && !isValid(dateB)) return 0;
      if (!isValid(dateA)) return 1;
      if (!isValid(dateB)) return -1;

      return dateA.getTime() - dateB.getTime();
    });
  }, [filteredOrders]);
  
  const ordersByStatus = React.useMemo(() => {
    const grouped: Record<string, Order[]> = {
      'in-progress': [],
      'ready': [],
      'completed': []
    };
    
    sortedOrders.forEach(order => {
      if (order.status === 'ready') {
        grouped['ready'].push(order);
      } else if (order.status === 'completed') {
        grouped['completed'].push(order);
      } else if (order.status === 'in_progress') {
        grouped['in-progress'].push(order);
      }
    });
    
    return grouped;
  }, [sortedOrders]);
  
  const isStaleOrder = (order: Order) => {
    if (order.status === 'ready' && order.ready_at) {
      const readyDate = parseISO(order.ready_at);
      const staleThreshold = subHours(new Date(), 24);
      return isAfter(staleThreshold, readyDate);
    }
    return false;
  };

  // Get the appropriate order title based on items
  const getOrderTitle = (order: Order) => {
    if (order.order_items && order.order_items.length > 1) {
      return `${order.order_items.length} items`;
    } else if (order.order_items && order.order_items.length === 1) {
      return order.order_items[0].meal.name;
    } else {
      return order.meal.name;
    }
  };

  // Render individual order items for multi-item orders
  const renderOrderItems = (order: Order) => {
    if (!order.order_items || order.order_items.length === 0) {
      return (
        <div className="mt-2 text-sm text-muted-foreground">
          <p>Item: {order.meal.name} × {order.quantity}</p>
        </div>
      );
    }

    return (
      <div className="mt-2 space-y-1">
        {order.order_items.map((item) => (
          <div key={item.id} className="text-sm text-muted-foreground flex justify-between">
            <span>{item.meal.name} × {item.quantity}</span>
            <span className="font-medium">${item.subtotal.toFixed(2)}</span>
          </div>
        ))}
      </div>
    );
  };
  
  const renderOrderCard = (order: Order) => {
    const isStale = isStaleOrder(order);
    const customerName = `${order.foodie.first_name} ${order.foodie.last_name}`.trim();
    const isSelected = selectedOrders.has(order.id);
    const totalItems = order.order_items ? order.order_items.length : 1;
    
    return (
      <Card 
        key={order.id} 
        className={`p-4 mb-4 relative ${isStale ? 'border-orange-300 bg-orange-50' : ''} ${isSelected ? 'border-blue-400 ring-2 ring-blue-200' : ''}`}
      >
        <div className="absolute top-4 left-4">
          <Checkbox 
            id={`select-${order.id}`}
            checked={isSelected}
            onCheckedChange={(checked) => handleOrderSelection(order.id, !!checked)}
            className="h-3.5 w-3.5 rounded-sm"
          />
        </div>
        
        <Link to={`/seller/orders/${order.id}`} className="ml-8 block">
          {isStale && (
            <div className="mb-2 flex items-center text-orange-600 text-sm">
              <AlertTriangle className="h-4 w-4 mr-1" />
              This order has been ready for over 24 hours
            </div>
          )}
          
          <div className="flex flex-col mb-3">
            <div className="flex items-center justify-between">
              <h3 className="font-medium text-lg line-clamp-1">{getOrderTitle(order)}</h3>
              {totalItems > 1 && (
                <Badge variant="outline" className="ml-2">
                  <Package className="h-3 w-3 mr-1" /> {totalItems} items
                </Badge>
              )}
            </div>
            
            <div className="mt-1">
              <Badge className={`${ORDER_STATUS_COLORS[order.status]} px-2 py-1`}>
                {ORDER_STATUS_LABELS[order.status]}
              </Badge>
            </div>
            
            <p className="text-sm text-muted-foreground mt-2">
              For: {customerName || 'Customer'}
            </p>
            
            {renderOrderItems(order)}
          </div>
          
          <div className="space-y-1 text-sm">
            <div className="flex items-center">
              <Clock className="h-4 w-4 mr-2 text-muted-foreground" />
              <span>
                {formatDateAndTime(order.scheduled_date, order.scheduled_time)}
              </span>
            </div>
            
            <div className="flex items-start">
              <Badge variant="outline" className="mr-2 h-6">
                {order.delivery_type === 'pickup' ? 'Pickup' : 'Delivery'}
              </Badge>
              <span className="text-sm line-clamp-1">
                {order.delivery_type === 'pickup' ? (
                  order.pickup_window_start && order.pickup_window_end ? 
                    `Window: ${formatTimeRange(order.pickup_window_start, order.pickup_window_end)}` : 
                    'No pickup window specified'
                ) : (
                  order.delivery_address || 'No delivery address'
                )}
              </span>
            </div>
          </div>
        </Link>
        
        <div className="mt-4 pt-4 border-t flex justify-between items-center">
          <OrderStatusActions 
            orderId={order.id}
            status={order.status} 
            onStatusChange={(newStatus) => handleStatusChange(order.id, newStatus)}
            readyAt={order.ready_at}
            compact={isMobile}
          />
          
          <Button variant="ghost" size="sm" asChild className="ml-2">
            <Link to={`/seller/orders/${order.id}`}>
              Details <ChevronRight className="h-4 w-4 ml-1" />
            </Link>
          </Button>
        </div>
      </Card>
    );
  };

  const renderBulkActions = () => {
    if (selectedOrders.size === 0) return null;
    
    const bulkActions: OrderStatus[] = [];
    const selectedOrderObjects = filteredOrders.filter(o => selectedOrders.has(o.id));
    
    const canMarkAsInProgress = selectedOrderObjects.some(o => canTransitionTo(o.status, "in_progress"));
    const canMarkAsReady = selectedOrderObjects.some(o => canTransitionTo(o.status, "ready"));
    const canMarkAsCompleted = selectedOrderObjects.some(o => canTransitionTo(o.status, "completed"));
    const canMarkAsCancelled = selectedOrderObjects.some(o => canTransitionTo(o.status, "cancelled"));
    
    if (canMarkAsInProgress) bulkActions.push("in_progress");
    if (canMarkAsReady) bulkActions.push("ready");
    if (canMarkAsCompleted) bulkActions.push("completed");
    if (canMarkAsCancelled) bulkActions.push("cancelled");
    
    return (
      <div className="bg-muted p-3 rounded-md mb-4 flex flex-col md:flex-row items-center justify-between">
        <div className="mb-3 md:mb-0">
          <span className="font-medium">{selectedOrders.size} orders selected</span>
        </div>
        
        <div className="flex flex-wrap gap-2">
          <select 
            className="px-3 py-2 bg-background border border-input rounded-md text-sm"
            value={bulkActionStatus}
            onChange={(e) => setBulkActionStatus(e.target.value as OrderStatus)}
          >
            <option value="">Select action...</option>
            {bulkActions.map(action => (
              <option key={action} value={action}>
                Mark as {ORDER_STATUS_LABELS[action]}
              </option>
            ))}
          </select>
          
          <Button 
            variant="default" 
            size="sm" 
            onClick={handleBulkStatusUpdate}
            disabled={!bulkActionStatus}
          >
            Apply <ArrowRight className="ml-1 h-4 w-4" />
          </Button>
          
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setSelectedOrders(new Set())}
          >
            Clear
          </Button>
        </div>
      </div>
    );
  };

  useEffect(() => {
    if (orders && orders.length > 0) {
      const hasOrderItems = orders.some(order => order.order_items && order.order_items.length > 0);
      logger.debug('Orders data summary:', { 
        total: orders.length,
        hasMultipleItems: hasOrderItems,
        sample: orders[0].id
      });
      
      if (hasOrderItems) {
        logger.debug('Multi-item order detected');
      }
    }
  }, [orders]);
  
  if (isLoading) {
    return <div className="py-8 text-center">Loading orders...</div>;
  }
  
  return (
    <div>
      {renderBulkActions()}
      
      <Tabs defaultValue="in-progress" value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid grid-cols-3">
          <TabsTrigger value="in-progress" className="flex items-center justify-center">
            <div className="flex items-center">
              <span>In Progress</span>
              <Badge variant="secondary" className="ml-1 text-xs py-0 px-1.5 h-5">
                {ordersByStatus['in-progress'].length}
              </Badge>
            </div>
          </TabsTrigger>
          
          <TabsTrigger value="ready" className="flex items-center justify-center">
            <div className="flex items-center">
              <span>Ready</span>
              <Badge variant="secondary" className="ml-1 text-xs py-0 px-1.5 h-5">
                {ordersByStatus['ready'].length}
              </Badge>
            </div>
          </TabsTrigger>
          
          <TabsTrigger value="completed" className="flex items-center justify-center">
            <div className="flex items-center">
              <span>Completed</span>
              <Badge variant="secondary" className="ml-1 text-xs py-0 px-1.5 h-5">
                {ordersByStatus['completed'].length}
              </Badge>
            </div>
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="in-progress" className="mt-0">
          {ordersByStatus['in-progress'].length === 0 ? (
            <div className="py-8 text-center text-muted-foreground">
              No orders in progress
            </div>
          ) : (
            ordersByStatus['in-progress'].map(renderOrderCard)
          )}
        </TabsContent>
        
        <TabsContent value="ready" className="mt-0">
          {ordersByStatus['ready'].length === 0 ? (
            <div className="py-8 text-center text-muted-foreground">
              No ready orders
            </div>
          ) : (
            ordersByStatus['ready'].map(renderOrderCard)
          )}
        </TabsContent>
        
        <TabsContent value="completed" className="mt-0">
          {ordersByStatus['completed'].length === 0 ? (
            <div className="py-8 text-center text-muted-foreground">
              No completed orders
            </div>
          ) : (
            ordersByStatus['completed'].map(renderOrderCard)
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};
